"use client";

import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import {
  CalendarCheck,
  Check,
  Clock,
  type LucideIcon,
  Mail,
  MessageSquare,
  Workflow,
  Zap,
} from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";

const EASE = [0.16, 1, 0.3, 1] as const;
const EASE_TRACE = [0.65, 0, 0.35, 1] as const;

type NodeStatus = "idle" | "running" | "done";

type FlowNode = {
  icon: LucideIcon;
  kind: string;
  title: string;
  doneLabel: string;
  tone?: "success";
};

// One end-to-end journey: enrolled by an event, nudged across channels, converts.
const JOURNEY: FlowNode[] = [
  { icon: Zap, kind: "Trigger", title: "Booked a first class", doneLabel: "Entered" },
  { icon: MessageSquare, kind: "Action", title: "Send welcome text", doneLabel: "Sent" },
  { icon: Clock, kind: "Wait", title: "Wait 1 day", doneLabel: "Done" },
  { icon: Mail, kind: "Action", title: "Email a membership offer", doneLabel: "Sent" },
  {
    icon: CalendarCheck,
    kind: "Outcome",
    title: "Joined membership",
    doneLabel: "Converted",
    tone: "success",
  },
];

// A single person runs the journey end to end, then the card rests on the
// converted outcome — one clear, complete story rather than an endless loop.
const PERSON = { name: "Maya Rivera", src: "/images/website/customers/maya-rivera.jpg" };

/* ================================================================== */
/* Flow card — the crisp, always-on journey (animated)                 */
/* ================================================================== */

// Vertical line that "draws" the accent downward over a gray track once the
// person has passed the node above it.
function Connector({ active, reduce }: { active: boolean; reduce: boolean }) {
  return (
    <div className="relative mx-auto h-4 w-px overflow-hidden rounded-full bg-border">
      <motion.div
        className="absolute inset-x-0 top-0 rounded-full bg-[hsl(var(--brand-accent))]"
        initial={reduce ? false : { height: "0%" }}
        animate={{ height: active ? "100%" : "0%" }}
        transition={{ duration: active && !reduce ? 0.5 : 0, ease: EASE_TRACE }}
      />
    </div>
  );
}

function NodeRow({
  node,
  status,
  person,
  reduce,
}: {
  node: FlowNode;
  status: NodeStatus;
  person: { name: string; src: string };
  reduce: boolean;
}) {
  const active = status !== "idle";
  const running = status === "running";
  const success = node.tone === "success";
  const Icon = node.icon;

  return (
    <div
      className={cn(
        "relative rounded-xl border border-border bg-background px-3 py-2.5 transition-all duration-500",
        active
          ? "opacity-100 shadow-[0_0_0_4px_hsl(var(--brand-accent)/0.08)]"
          : "opacity-45 shadow-sm",
      )}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-md border border-border transition-colors",
            success && active
              ? "bg-success/10 text-success"
              : "bg-background text-muted-foreground",
          )}
        >
          <Icon className="size-3.5" />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {node.kind}
          </p>
          <p className="truncate text-sm font-semibold text-foreground">{node.title}</p>
        </div>

        {/* Right rail: the live person while running, a status check once done. */}
        <div className="ml-auto flex shrink-0 items-center">
          <AnimatePresence mode="popLayout" initial={false}>
            {running ? (
              <motion.span
                key="running"
                initial={reduce ? false : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduce ? undefined : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2, ease: EASE }}
                className="flex items-center gap-1.5"
              >
                <Avatar name={person.name} src={person.src} size="xs" />
                <span className="text-[11px] font-medium text-muted-foreground">now</span>
              </motion.span>
            ) : status === "done" ? (
              <motion.span
                key="done"
                initial={reduce ? false : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduce ? undefined : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2, ease: EASE }}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-foreground"
              >
                <Check className="size-3 text-success" />
                {node.doneLabel}
              </motion.span>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export function FlowCard({ className }: { className?: string }) {
  const reduce = useReducedMotion() ?? false;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.4 });
  const [step, setStep] = useState(0);

  const last = JOURNEY.length - 1;

  // Run the person through once: advance step by step, then take one extra step
  // past the outcome so it settles on "Converted" and stays there.
  useEffect(() => {
    if (reduce || !inView || step > last) return;
    const id = setTimeout(() => setStep((s) => s + 1), 1100);
    return () => clearTimeout(id);
  }, [step, inView, reduce, last]);

  const person = PERSON;
  const statusFor = (i: number): NodeStatus => {
    if (reduce) return "done";
    if (i < step) return "done";
    if (i === step) return "running";
    return "idle";
  };
  const connectorActive = (i: number) => (reduce ? true : step >= i);

  return (
    <div
      ref={ref}
      className={cn(
        "overflow-hidden rounded-2xl border border-border bg-background shadow-2xl shadow-black/10 dark:shadow-black/50",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/50 text-muted-foreground">
            <Workflow className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold text-foreground">New member journey</p>
            <p className="truncate text-[11px] text-muted-foreground">Always on · 1,240 enrolled</p>
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-foreground">
          <span className="size-1.5 animate-pulse rounded-full bg-green-500" />
          Live
        </span>
      </div>

      {/* Journey */}
      <div className="flex flex-col px-4 py-4">
        {JOURNEY.map((node, i) => (
          <Fragment key={node.title}>
            {i > 0 ? <Connector active={connectorActive(i)} reduce={reduce} /> : null}
            <NodeRow node={node} status={statusFor(i)} person={person} reduce={reduce} />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/* ================================================================== */
/* Enrolled panel — the supporting "always on, at scale" visual        */
/* ================================================================== */

type EnrolledRow = {
  name: string;
  src: string;
  step: string;
  tone: "brand" | "muted" | "success";
};

const ENROLLED: EnrolledRow[] = [
  {
    name: "Devin Carter",
    src: "/images/website/customers/devin-carter.jpg",
    step: "Joined",
    tone: "success",
  },
  {
    name: "Marissa Larimer",
    src: "/images/website/customers/marissa-larimer.jpg",
    step: "Value email",
    tone: "brand",
  },
  {
    name: "Casey Lin",
    src: "/images/website/customers/casey-lin.jpg",
    step: "Wait 1 day",
    tone: "muted",
  },
  {
    name: "Priya Nair",
    src: "/images/website/customers/priya-nair.jpg",
    step: "Welcome text",
    tone: "brand",
  },
  {
    name: "Maya Rivera",
    src: "/images/website/customers/maya-rivera.jpg",
    step: "Entered",
    tone: "muted",
  },
];

const STEP_CHIP: Record<EnrolledRow["tone"], string> = {
  brand: "bg-primary/10 text-primary dark:bg-brand-accent/10 dark:text-brand-accent",
  muted: "bg-muted text-muted-foreground",
  success: "bg-success/10 text-success",
};

export function EnrolledPanel({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border border-border bg-background",
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="text-[13px] font-semibold text-foreground">In flow now</span>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-foreground">
          <span className="size-1.5 animate-pulse rounded-full bg-green-500" />
          Live
        </span>
      </div>
      <div className="divide-y divide-border">
        {ENROLLED.map((row) => (
          <div key={row.name} className="flex items-center gap-2.5 px-4 py-2.5">
            <Avatar name={row.name} src={row.src} size="sm" />
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
              {row.name}
            </span>
            <span
              className={cn(
                "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
                STEP_CHIP[row.tone],
              )}
            >
              {row.step}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
